import axios from 'axios';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

let refreshPromise = null;

function emitWindowEvent(name, detail = {}) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function clearStoredSession({ keepDeviceId = false } = {}) {
  localStorage.removeItem('notas_access_token');
  localStorage.removeItem('notas_refresh_token');
  if (!keepDeviceId) {
    localStorage.removeItem('notas_device_id');
  }
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('notas_access_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export async function refreshSession() {
  const refreshToken = localStorage.getItem('notas_refresh_token');
  const deviceId = localStorage.getItem('notas_device_id');

  if (!refreshToken || !deviceId) {
    throw new Error('No hay sesión para renovar.');
  }

  const response = await axios.post(
    `${String(api.defaults.baseURL || '/api').replace(/\/$/, '')}/auth/refresh`,
    { refresh_token: refreshToken, device_id: deviceId },
    { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
  );

  const data = response.data?.data;
  if (!data?.access_token || !data?.refresh_token) {
    throw new Error('La renovación de sesión no devolvió tokens válidos.');
  }

  localStorage.setItem('notas_access_token', data.access_token);
  localStorage.setItem('notas_refresh_token', data.refresh_token);
  localStorage.setItem('notas_device_id', data.device_id || deviceId);

  emitWindowEvent('notas:token-refreshed', {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    device_id: data.device_id || deviceId,
  });

  return data.access_token;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;
    const url = String(originalRequest.url || '');
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh');

    if (status !== 401 || isAuthEndpoint || originalRequest._retry) {
      return Promise.reject(error);
    }

    try {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = refreshSession().finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      clearStoredSession();
      emitWindowEvent('notas:auth-invalid', {
        reason: refreshError?.response?.data?.message || refreshError?.message || 'Sesión inválida.',
      });
      return Promise.reject(refreshError);
    }
  }
);

export default api;
